import { Select } from '@profgeosoft-ui/react';
import clsx from 'clsx';
import { reaction } from 'mobx';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ComboboxDropdown } from 'src/components/combobox-dropdown';
import { wrap } from 'src/packages/mobx-di/wrap';

import type { RefSelectProps } from '@profgeosoft-ui/react';
import type { MutableRefObject } from 'react';
import type { TOption } from 'src/packages/types';
import type { OptionsService } from 'src/services/directory-service/options-service';
import type { TFieldRulesData, TMultiComboBoxView } from 'src/services/directory-service/types';

import styles from './multicombobox.module.scss';

type Props = {
  controlView: TMultiComboBoxView;
  optionsService: OptionsService;
  value: (number | string)[];
  error?: string;
  fieldRulesData?: TFieldRulesData;
  className?: string;
  innerRef?: MutableRefObject<RefSelectProps | null>;
  loadOptionsFn: (signal?: AbortSignal) => Promise<TOption[]>;
  onChange(fieldId: string, value: unknown): void;
  onBlur?(fieldId: string): void;
  onCreateNewRecord: () => void;
};

export const MulticomboboxComponent = wrap(function MulticomboboxComponent({
  innerRef,
  controlView,
  optionsService,
  value,
  error,
  fieldRulesData,
  className,
  loadOptionsFn,
  onCreateNewRecord,
  onChange,
  onBlur,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [internalOptions, setInternalOptions] = useState<TOption[]>(optionsService.options[controlView.fieldId] ?? []);
  const [isLoading, setIsLoading] = useState(false);

  const optionsAsValue = (() => {
    const valueObjects: TOption[] = [];

    for (const _value of value) {
      const correspondingOption =
        optionsService.optionsMap[controlView.fieldId].get(_value) ??
        optionsService.archivedOptionsMap[controlView.fieldId].get(_value);

      if (!correspondingOption) {
        continue;
      }

      valueObjects.push(correspondingOption);
    }

    return valueObjects;
  })();

  const loadOptions = useCallback((): VoidFunction => {
    const abortController = new AbortController();

    const load = async () => {
      setIsLoading(true);
      const options = await loadOptionsFn(abortController.signal);
      setInternalOptions(options);
      setIsLoading(false);
    };

    load();

    return () => abortController.abort();
  }, [loadOptionsFn]);

  const handleChange = (rawValue: (number | TOption)[]) => {
    const processedValues: number[] = [];

    for (const value of rawValue) {
      if (typeof value === 'number') {
        processedValues.push(value);
        continue;
      }

      processedValues.push(value.value);
    }

    onChange(controlView.fieldId, processedValues);

    setTimeout(() => ref.current?.scrollIntoView(), 0);
  };

  const handleBlur = () => {
    onBlur?.(controlView.fieldId);
  };

  useEffect(() => {
    if (!controlView.refQuery) {
      return;
    }

    return loadOptions();
  }, [controlView.refQuery, loadOptions]);

  useEffect(() => {
    return reaction(
      () => optionsService.options[controlView.fieldId] ?? [],
      (options) => setInternalOptions(options),
      { fireImmediately: true },
    );
  }, [controlView.fieldId, optionsService.options]);

  const isOpened =
    isOpen &&
    !fieldRulesData?.isLoading &&
    !isLoading &&
    !fieldRulesData?.isDisabled &&
    !fieldRulesData?.isEnteringBlocked;

  return (
    <div ref={ref}>
      <Select<TOption[] | number[]>
        ref={innerRef}
        open={isOpened}
        value={isLoading || fieldRulesData?.isLoading ? [] : optionsAsValue}
        options={internalOptions}
        dropdownMatchSelectWidth={false}
        className={clsx(styles.multicombobox, className)}
        allowClear
        optionFilterProp="label"
        showSearch
        loading={fieldRulesData?.isLoading || isLoading}
        disabled={fieldRulesData?.isDisabled || fieldRulesData?.isEnteringBlocked}
        placeholder={t('common:placeholders.chooseValue')}
        mode="multiple"
        status={error ? 'error' : undefined}
        onDropdownVisibleChange={(open) => setIsOpen(open)}
        dropdownRender={(menu) => (
          <ComboboxDropdown onButtonClick={onCreateNewRecord} onClose={() => setIsOpen(false)}>
            {menu}
          </ComboboxDropdown>
        )}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    </div>
  );
});
